import { useForm, Controller } from 'react-hook-form';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChannelsService } from '../../../../services/channels.service';
import { AlertService } from '../../../../services/alert.service';
import useInputValidation from '../../../../hooks/use-input-validation.hook';
import Select from 'react-select';
import Input from '../../../../components/forms/input.component';
import Button from '../../../../components/forms/button.component';
import Title from '../../../../components/forms/titles.component';
import InvalidFeedback from '../../../../components/forms/invalid-feedback.component';
import useModal from '../../../../hooks/use-close-modal.hook';
import LogData from '../../../../components/logs/log-data.component';
import Icon from '../../../../components/forms/icon.component';

interface ProviderType {
  value: string;
  label: string;
  settings_definition: {
    [key: string]: {
      type: string;
      required: boolean;
    };
  };
}
interface AddMailingAccountProps {
  show: boolean;
  onRequestClose: (refresh: boolean) => void;
  payload: any;
}

type AddMailingFormValues = {
  _id: string;
  provider: string;
  name: string;
  settings: {
    [key: string]: any;
  };
};

function AddMailingAccount({ show, onRequestClose, payload }: AddMailingAccountProps) {
  const { t } = useTranslation();
  const modalRef = useRef<HTMLDivElement>(null);
  const { emailRegex } = useInputValidation();

  const [providers, setProviders] = useState([]);
  const [selectedProvider, setSelectedProvider] = useState<ProviderType | null>(null);

  useModal(modalRef, show);

  const defaultValues: AddMailingFormValues = {
    _id: '',
    provider: '',
    name: '',
    settings: {}
  };

  const {
    register,
    control,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors, isValid, isSubmitting }
  } = useForm<AddMailingFormValues>({
    mode: 'onChange',
    defaultValues
  });

  const getFieldPlaceholder = (fieldName: string) => {
    const placeholderMap: { [key: string]: string } = {
      host: t('channels.mailing.modal.host-placeholder'),
      port: t('channels.mailing.modal.port-placeholder'),
      name_from: t('channels.mailing.modal.name-from-placeholder'),
      email_from: t('channels.mailing.modal.email-from-placeholder'),
      user: t('channels.mailing.modal.user-placeholder'),
      password: t('channels.mailing.modal.password-placeholder'),
      domain: 'Ingresa el dominio',
      username: 'Ingresa el nombre de usuario',
      smtp_server: 'Ingresa el servidor SMTP'
    };

    return placeholderMap[fieldName] || `Ingresa ${fieldName.replace('_', ' ')}`;
  };

  const getFieldLabel = (fieldName: string) => {
    const labelMap: { [key: string]: string } = {
      host: t('channels.mailing.modal.host-label'),
      port: t('channels.mailing.modal.port-label'),
      name_from: t('channels.mailing.modal.name-from-label'),
      email_from: t('channels.mailing.modal.email-from-label'),
      user: t('channels.mailing.modal.user-label'),
      password: t('channels.mailing.modal.password-label'),
      domain: 'Dominio',
      username: 'Nombre de usuario',
      smtp_server: 'Servidor SMTP'
    };

    return (
      labelMap[fieldName] || fieldName.replace('_', ' ').replace(/\b\w/g, (l) => l.toUpperCase())
    );
  };

  const getInputType = (fieldName: string) => {
    if (fieldName.includes('password') || fieldName.includes('secret')) return 'password';
    if (fieldName.includes('email')) return 'email';
    if (fieldName.includes('port')) return 'number';
    return 'text';
  };

  const getValidationRules = (fieldName: string, fieldDef: any) => {
    const rules: any = {
      required: fieldDef.required,
      setValueAs: (value: string) => value.trim()
    };

    if (fieldName.includes('email')) {
      rules.pattern = {
        value: emailRegex,
        message: t('channels.mailing.validation.email-from-valid')
      };
    }

    if (fieldName.includes('port')) {
      rules.pattern = {
        value: /^\d+$/,
        message: t('channels.mailing.validation.port-number')
      };
    }

    return rules;
  };

  const renderDynamicFields = () => {
    if (!selectedProvider || !selectedProvider.settings_definition) {
      return (
        <div className="alert alert-info">
          <Icon icon="ki-information-5" className="me-2" type="info" />
          {t('channels.mailing.modal.select-provider-message')}
        </div>
      );
    }

    const fields = Object.entries(selectedProvider.settings_definition);

    return (
      <div className="row">
        {fields.map(([fieldName, fieldDef], index) => (
          <div key={fieldName} className={getFieldColumnClass(fieldName, index, fields.length)}>
            <div className="fv-row mb-7">
              <Input
                label={getFieldLabel(fieldName)}
                requiredIndicator={fieldDef.required ? 'required' : undefined}
                type={getInputType(fieldName)}
                placeholder={getFieldPlaceholder(fieldName)}
                errors={(errors?.settings as Record<string, any> | undefined)?.[fieldName]}
                {...register(`settings.${fieldName}`, getValidationRules(fieldName, fieldDef))}
              />
            </div>
          </div>
        ))}
      </div>
    );
  };

  const getFieldColumnClass = (fieldName: string, index: number, totalFields: number) => {
    if ((fieldName === 'host' || fieldName === 'port') && totalFields > 2) {
      return fieldName === 'host' ? 'col-md-8 pe-3' : 'col-md-4';
    }
    return 'col-12';
  };

  const registerAccount = async (data: any, buttonId: string) => {
    try {
      if (payload != null) {
        const confirmed = await AlertService.question(
          t('general.atention'),
          t('channels.mailing.confirmations.edit-message', { name: payload?.name }),
          null as any
        );
        if (!confirmed) {
          return;
        }
        await ChannelsService.updateMailingAccount(data);
        AlertService.toastSuccess(t('channels.mailing.messages.update-success'));
      } else {
        const confirmed = await AlertService.question(
          t('general.atention'),
          t('channels.mailing.confirmations.add-message'),
          null as any
        );
        if (!confirmed) {
          return;
        }
        delete data._id;
        await ChannelsService.saveMailingAccount(data);
        AlertService.toastSuccess(t('channels.mailing.messages.add-success'));
      }
      LogData(data, buttonId, window.location.href);
      onRequestClose(true);
      reset(defaultValues);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const resetForm = () => {
    onRequestClose(false);
    reset(defaultValues);
    setSelectedProvider(null);
  };

  const getProviders = async () => {
    try {
      const data = await ChannelsService.getMailingProviders();
      const formattedProviders = data.map((item: any) => ({
        value: item._id,
        label: item.name,
        settings_definition: item.settings_definition
      }));
      setProviders(formattedProviders);
    } catch (errorCode: any) {
      console.log('Error:', errorCode);
      AlertService.toastError(errorCode.error || errorCode);
    }
  };

  const handleProviderChange = (providerValue: string | null) => {
    const provider = providers.find((p: any) => p.value === providerValue) || (null as any);
    setSelectedProvider(provider);
    setValue('settings', {});

    if (provider && provider.settings_definition) {
      const newSettings: any = {};
      Object.keys(provider.settings_definition).forEach((key) => {
        newSettings[key] = '';
      });
      setValue('settings', newSettings);
    }
  };

  useEffect(() => {
    getProviders();
  }, []);

  useEffect(() => {
    if (payload) {
      const provider = providers.find((p: any) => p.value === payload.provider);
      setSelectedProvider(provider || null);

      reset({
        _id: payload._id || defaultValues._id,
        provider: payload.provider || defaultValues.provider,
        name: payload.name || defaultValues.name,
        settings: payload.settings || {}
      });
    } else {
      setSelectedProvider(null);
      reset(defaultValues);
    }
  }, [payload, reset, providers]);

  return (
    <div className="modal fade" ref={modalRef} data-bs-backdrop="static">
      <div className="modal-dialog mw-1000px">
        <div className="modal-content">
          <div className="modal-header justify-content-end border-0 pb-0">
            <div
              className="btn btn-sm btn-icon btn-active-light-primary"
              onClick={() => resetForm()}
            >
              <Icon icon="ki-cross" size="fs-2x" />
            </div>
          </div>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              const buttonID = watch('_id')
                ? 'editMailingAccount-7326478503'
                : 'addMailingAccount-3394857490';
              handleSubmit((data) => registerAccount(data, buttonID))(e);
            }}
          >
            <div className="modal-body pt-0 pb-15 px-5 px-xl-20">
              <div className="mt-1 text-center">
                {watch('_id') ? (
                  <>
                    <h1>
                      {t('channels.mailing.modal.edit-title')} <br />
                      <small className="text-primary fw-semibold fs-2x ms-2">{payload?.name}</small>
                    </h1>
                  </>
                ) : (
                  <h1>{t('channels.mailing.modal.add-title')}</h1>
                )}
                <div className="text-muted fw-semibold fs-5">
                  <span
                    dangerouslySetInnerHTML={{ __html: t('channels.rcs.modal-C-Subtitle') }}
                  ></span>
                </div>
                <div className="separator border-2 my-5"></div>
              </div>

              <div className="row my-5 px-2">
                <div className="col-lg-6 mb-10 mb-lg-0 h-100 p-10">
                  <Title
                    icon={'ki-notepad-edit'}
                    title={t('channels.mailing.modal.general-data')}
                    subtitle={
                      watch('_id')
                        ? t('channels.mailing.modal.general-data-subtitle-edit')
                        : t('channels.mailing.modal.general-data-subtitle-add')
                    }
                  />

                  <div className="fv-row mb-7">
                    <Input
                      label={t('channels.mailing.modal.name-label')}
                      requiredIndicator="required"
                      placeholder={t('channels.mailing.modal.name-placeholder')}
                      errors={errors.name}
                      {...register('name', {
                        required: true,
                        setValueAs: (value) => value.trim()
                      })}
                    />
                  </div>

                  <div className="fv-row mb-7">
                    <label className="form-label required">
                      {t('channels.mailing.modal.provider-label')}
                    </label>
                    <Controller
                      control={control}
                      name="provider"
                      rules={{ required: true }}
                      render={({ field: { onChange, value }, fieldState: { error } }) => (
                        <>
                          <Select
                            options={providers}
                            noOptionsMessage={({ inputValue }) =>
                              inputValue
                                ? t('channels.mailing.modal.provider-no-search', {
                                    search: inputValue
                                  })
                                : t('channels.mailing.modal.provider-no-options')
                            }
                            className={
                              error ? 'form-select-custom is-invalid' : 'form-select-custom'
                            }
                            classNamePrefix="form-select-custom"
                            placeholder={t('channels.mailing.modal.provider-placeholder')}
                            onChange={(selectedOption: ProviderType | null) => {
                              const providerValue = selectedOption ? selectedOption.value : null;
                              onChange(providerValue);
                              handleProviderChange(providerValue);
                            }}
                            value={
                              providers.find((option: ProviderType) => option.value === value) ||
                              null
                            }
                          />
                          <InvalidFeedback error={error} />
                        </>
                      )}
                    />
                  </div>
                </div>

                <div className="col-lg-6 mb-10 mb-lg-0 h-100 p-10">
                  <Title
                    icon={'ki-setting-2'}
                    type={'info'}
                    title={t('channels.mailing.modal.general-data')}
                    subtitle={
                      watch('_id')
                        ? t('channels.mailing.modal.general-data-subtitle-edit')
                        : t('channels.mailing.modal.general-data-subtitle-add')
                    }
                  />
                  {renderDynamicFields()}
                </div>
              </div>

              <div className="d-flex flex-center flex-row-fluid pt-2">
                <button
                  type="button"
                  className="btn btn-light hover-scale"
                  onClick={() => resetForm()}
                >
                  {t('general.close')}
                </button>
                <Button
                  type="submit"
                  id="sendData"
                  className="btn btn-primary ms-3 hover-scale"
                  disabled={!isValid || !selectedProvider}
                  isLoading={isSubmitting}
                >
                  {t('general.save')}
                  <Icon icon="ki-send" size="fs-1" className="ms-2" />
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default AddMailingAccount;
